import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import * as turf from '@turf/turf';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {ToastNotification} from 'app/modules/Notification';
import {api} from 'app/modules/Remote';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiDollarUnitUsage, ApiImageryContract} from 'app/modules/Remote/Project';
import {useImageryPrices} from 'app/providers/ImageryPricesProvider';
import {recordEvent} from 'app/tools/Analytics';
import {ApiResponseError} from 'app/utils/apiUtils';
import * as CONSTANTS from 'app/utils/constants';
import * as C from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import {calculateCost, formatCost} from 'app/utils/costUtils';
import * as featureUtils from 'app/utils/featureUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import {calculateBillableUTMAreaInAcres} from 'app/utils/geoJsonUtils';
import {
  CHLORIS_10_PACKAGE,
  CHLORIS_30_PACKAGE,
  LAYER_PACKAGES,
  PLANET_FOREST_DILIGENCE_PACKAGE,
  PLANET_FOREST_MONITORING_PACKAGE,
} from 'app/utils/layers';
import {getLayer, isLayerKeyRaw, parseLayerKey} from 'app/utils/layerUtils';
import * as userUtils from 'app/utils/userUtils';

import cs from './styles.styl';

import {DollarUnitContractDetails} from '.';

/**
 * Helper function callers can use to actually order imagery. Returns the
 * updated feature if the purchase API request was successful, otherwise null.
 */

export async function orderImagery(
  projectId: string,
  featureCollectionId: number,
  featureId: number,
  orderableScene: I.ImmutableOf<ApiOrderableScene>,
  billableAcres: number,
  centsPerAcre: number,
  refreshCentsPerAcre: () => void,
  pushNotification: (p: ToastNotification) => void,
  isOrderingSubset: boolean,
  subsetFeatureGeometry?: geojson.Polygon | undefined
) {
  try {
    // TODO(fiona): invalidate imagery purchases and the imagery contract,
    // since ordering imagery changes them.
    // Only include geometry in payload if subset geometry is passed in
    const data = {
      sourceId: orderableScene.get('sourceId'),
      sensingTime: orderableScene.get('sensingTime'),
      billableAcres,
      centsPerAcre,
      ...(!!subsetFeatureGeometry && isOrderingSubset && {geometry: subsetFeatureGeometry}),
    };

    const updatedFeature = (
      await api.featureCollections
        .features(featureCollectionId)
        .orderImagery(projectId, featureId, data)
    ).get('data');

    recordEvent('Ordered an image', {
      projectId,
      featureCollectionId,
      featureId,
      sourceId: orderableScene.get('sourceId'),
      sensingTime: orderableScene.get('sensingTime'),
    });

    pushNotification({
      message:
        'The requested data has been ordered. You will receive an email when processing is complete.',
      autoHideDuration: 5000,
    });

    return updatedFeature;
  } catch (e) {
    const {error} = e as ApiResponseError;

    Sentry.captureException(error, {
      extra: {
        featureId: featureId,
        orderableSceneSource: orderableScene.get('sourceId'),
        orderableSceneSensingTime: orderableScene.get('sensingTime'),
        subsetGeometry: JSON.stringify(subsetFeatureGeometry ?? null),
      },
    });

    if (error.message.includes('Invalid price')) {
      refreshCentsPerAcre();
      pushNotification({
        message:
          'Error ordering the requested data. The price of this image has changed. Please try again',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } else if (error.message.includes('Requested billable acres mismatch')) {
      // Often this happens because we updated the minimum_order_acres for a source but the user hasn't refreshed Lens since that change so
      // the billable_acres we send in the api request uses the old value for minimum_order_acres
      pushNotification({
        message:
          'Error ordering the requested data. Please refresh Lens and try again. Our team has been notified and we are on it! ',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } else {
      pushNotification({
        message: 'Error ordering the requested data. Our team has been notified and we are on it!',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    }

    return null;
  }
}

/*
 * Helper function callers can use to actually order bulk imagery
 */

export async function orderBulkImagery(
  projectId: string,
  featureCollectionId: number,
  featureIds: number[],
  sourceId: string,
  sensingTime: string,
  expectedCostInCents: number,
  pushNotification: (p: ToastNotification) => void
) {
  try {
    await api.featureCollections
      .features(featureCollectionId)
      .orderImageryBulk(projectId, featureIds, sensingTime, sourceId, expectedCostInCents);

    recordEvent('Placed a bulk order', {
      projectId,
      featureCollectionId,
      featureIds: featureIds.join(','),
      sourceId,
      sensingTime,
    });

    pushNotification({
      message:
        'The requested data has been ordered. You will receive an email when processing is complete.',
      autoHideDuration: 5000,
      options: {intent: B.Intent.SUCCESS},
    });
  } catch (e) {
    const {error} = e as ApiResponseError;

    Sentry.captureException(error, {
      extra: {
        featureIds,
        sourceId,
        sensingTime,
        billableCost: expectedCostInCents,
      },
    });

    if (error.message.includes('Total cost mismatch')) {
      pushNotification({
        message:
          'Error ordering the requested data. The price of this data has changed. Please try again',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } else {
      pushNotification({
        message: 'Error ordering the requested data. Our team has been notified and we are on it!',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    }

    return null;
  }
}

export function canPurchaseOnContract(
  imageryContract: ApiImageryContract,
  acreage: number,
  pricePerAcre: number
) {
  const usage = imageryContract.usage as ApiDollarUnitUsage;

  if (acreage <= 0) {
    return false;
  }

  if (usage.nte_cents === null) {
    return true;
  } else {
    const usedCents = usage.purchased_cents;
    const currentOrderCents = calculateCost(acreage, pricePerAcre);

    return usedCents + currentOrderCents <= usage.nte_cents;
  }
}

export function getBillableAcres(
  feature: I.ImmutableOf<ApiFeature>,
  orderableScene: I.ImmutableOf<ApiOrderableScene>,
  subsetFeature: geojson.Feature<geojson.Polygon> | null
) {
  const orderGeometryAcres = getOrderGeometryAcres(feature, orderableScene, subsetFeature);
  const minimumAcres =
    C.SOURCE_DETAILS_BY_ID[orderableScene.get('sourceId')]?.minimum_order_acres || 0;
  const billableAcres = Math.max(orderGeometryAcres, minimumAcres);
  return {orderGeometryAcres, billableAcres};
}
export function getOrderGeometryAcres(
  feature: I.ImmutableOf<ApiFeature>,
  orderableScene: I.ImmutableOf<ApiOrderableScene>,
  subsetFeature: geojson.Feature<geojson.Polygon> | null
) {
  // Calculate billableAcres if we are ordering partial imagery or if
  // partial imagery has already been ordered, otherwise just use the information
  // on the feature
  if (!orderableScene.get('orderedGeometry') && !subsetFeature) {
    const sceneCoverage = featureUtils.getSceneCoverage(orderableScene, feature);
    const featureGeometry = feature.get('geometry').toJS();

    // Calculate area using UTM projection to match backend
    const areaInAcres = calculateBillableUTMAreaInAcres(featureGeometry) * sceneCoverage;

    // Apply the same rounding logic as backend: round down and ensure minimum of 1 acre
    return Math.max(1, Math.floor(areaInAcres));
  }

  // Rounds to decimal to 6 places
  const roundDecimalPlace = (position: geojson.Position) =>
    position.map((num) => Math.floor(num * 1000000) / 1000000);

  // Start with geometry of the feature
  let billableGeometry: geojson.Polygon | geojson.MultiPolygon | undefined = feature
    .get('geometry')
    .toJS();

  // Round all coordinates to 6 decimal places to avoid floating point math errors in turf
  // calculations
  billableGeometry = geoJsonUtils.applyToCoordinates(billableGeometry, roundDecimalPlace);

  try {
    // If a subset is being ordered take the intersection of the subset geometry and the feature
    if (subsetFeature && feature) {
      const subsetFeatureGeometry = subsetFeature.geometry;
      billableGeometry =
        billableGeometry &&
        geoJsonUtils.intersectGeometries(subsetFeatureGeometry, billableGeometry)?.geometry;
    }

    // If some geometry has already been ordered for a given scene, remove that area from
    // the billableGeometry
    if (orderableScene.get('orderedGeometry')) {
      let alreadyOrderedGeometry = orderableScene.get('orderedGeometry')?.toJS() as
        | geojson.Polygon
        | geojson.MultiPolygon
        | undefined;

      // Round all coordinates to 6 decimal places to avoid floating point math errors in turf
      // calculations
      alreadyOrderedGeometry = geoJsonUtils.applyToCoordinates(
        alreadyOrderedGeometry,
        roundDecimalPlace
      );

      billableGeometry =
        billableGeometry &&
        alreadyOrderedGeometry &&
        turf.difference(
          turf.featureCollection([
            turf.feature(billableGeometry),
            turf.feature(alreadyOrderedGeometry),
          ])
        )?.geometry;
    }

    // Lastly take the intersection of the previous steps with the geometry of the scene
    const sceneGeometry = orderableScene.get('sceneGeometry').toJS() as
      | geojson.Polygon
      | geojson.MultiPolygon;

    // Calculate area using our UTM-based approach to match backend
    let billableAcres = 0;
    if (billableGeometry) {
      // Get the intersection between billable geometry and scene geometry
      const intersectionFeature = geoJsonUtils.intersectGeometries(billableGeometry, sceneGeometry);

      if (intersectionFeature) {
        // Use a UTM projection-based area calculation to match backend
        billableAcres = calculateBillableUTMAreaInAcres(intersectionFeature.geometry);
      }
    }

    return billableAcres;
  } catch (e) {
    // Calculating the difference/intersection for features that have very small differences may fail.
    // This should be handled by the API and treated as a fully ordered feature, but if not we want
    // to catch it.
    Sentry.captureException(e, {
      extra: {
        featureId: feature.get('id'),
        orderableSceneSource: orderableScene.get('sourceId'),
        orderableSceneSensingTime: orderableScene.get('sensingTime'),
        subsetGeometry: JSON.stringify(subsetFeature?.geometry ?? null),
      },
    });
    console.error(e);
    return -1;
  }
}

export function formatAcres(acreage: number): string {
  return conversionUtils.numberWithCommas(acreage.toString());
}

export const ORDERABLE_SCENE_ERRORS = {
  ORDER_STILL_PROCESSING: 'Order is still processing',
  UNABLE_TO_ORDER: 'Unable to order for this property',
};

export function validateOrderableScene(orderableScene: ApiOrderableScene): {error?: string} {
  if (orderableScene.isOrdered && !orderableScene.orderProcessed) {
    return {error: ORDERABLE_SCENE_ERRORS.ORDER_STILL_PROCESSING};
  }
  if (!orderableScene.canBeOrdered) {
    return {error: ORDERABLE_SCENE_ERRORS.UNABLE_TO_ORDER};
  }
  return {};
}

export const getPackageOrderText = (layerKey: string, isBulkOrder: boolean = false) => {
  const PACKAGE_ORDER_TEXT: {[layerKey: string]: React.ReactNode} = {
    [PLANET_FOREST_DILIGENCE_PACKAGE]: (
      <>
        <p>
          The Planet Forest Carbon Diligence Package includes the following three layers. If this is
          the first time you're purchasing data for{' '}
          {isBulkOrder ? 'a property in this selection' : 'this property'}, the full archive of
          annual data back to 2013 will be included at no extra cost.
        </p>
        <ol>
          {LAYER_PACKAGES[PLANET_FOREST_DILIGENCE_PACKAGE].filter(
            (layerKey) => !isLayerKeyRaw(layerKey)
          ).map((layerKey) => (
            <li key={layerKey}>{getLayer(layerKey).display}</li>
          ))}
        </ol>
        <p>
          See our{' '}
          <a
            href="https://support.upstream.tech/article/157-planet-forest-carbon-diligence"
            target="_blank"
            rel="noopener noreferrer"
          >
            support doc here
          </a>{' '}
          for more details.
        </p>
      </>
    ),
    [PLANET_FOREST_MONITORING_PACKAGE]: (
      <>
        <p>
          The Planet Forest Carbon Monitoring Package includes the following three layers at 3m
          resolution. If this is the first time you're purchasing data for{' '}
          {isBulkOrder ? 'a property in this selection' : 'this property'}, the archive of quarterly
          data back to 2021 will be included at no extra cost.
        </p>
        <ol>
          {LAYER_PACKAGES[PLANET_FOREST_MONITORING_PACKAGE].filter(
            (layerKey) => !isLayerKeyRaw(layerKey)
          ).map((layerKey) => (
            <li key={layerKey}>{getLayer(layerKey).display}</li>
          ))}
        </ol>
        <p>
          See our{' '}
          <a
            href="https://support.upstream.tech/article/157-planet-forest-carbon-diligence#3meter"
            target="_blank"
            rel="noopener noreferrer"
          >
            support doc here
          </a>{' '}
          for more details.
        </p>
      </>
    ),
    [CHLORIS_30_PACKAGE]: (
      <>
        <p>
          The Chloris 30m package includes data on the following layers. If this is your first
          purchase for {isBulkOrder ? 'a property in this selection' : 'this property'}, the full
          archive of annual data back to 2000 will be included at no extra cost.
        </p>
        <ol>
          {LAYER_PACKAGES[CHLORIS_30_PACKAGE].filter((layerKey) => !isLayerKeyRaw(layerKey)).map(
            (layerKey) => (
              <li key={layerKey}>{getLayer(layerKey).display}</li>
            )
          )}
        </ol>
        <p>
          Chloris data is not pre-processed and may take up to 5 business days to process and
          deliver into your account. By placing this order, you agree to comply with the{' '}
          <a
            href="https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Chloris End User License Agreement linked here.
          </a>
        </p>
        <p>
          See our{' '}
          <a
            href="https://support.upstream.tech/article/170-chloris-forest-carbon"
            target="_blank"
            rel="noopener noreferrer"
          >
            support doc
          </a>{' '}
          for more details.
        </p>
      </>
    ),
    [CHLORIS_10_PACKAGE]: (
      <>
        <p>
          The Chloris 10m package includes data on the following layers. If this is your first
          purchase for {isBulkOrder ? 'a property in this selection' : 'this property'}, the full
          archive of annual data back to 2017 will be included at no extra cost.
        </p>
        <ol>
          {LAYER_PACKAGES[CHLORIS_10_PACKAGE].filter((layerKey) => !isLayerKeyRaw(layerKey)).map(
            (layerKey) => (
              <li key={layerKey}>{getLayer(layerKey).display}</li>
            )
          )}
        </ol>
        <p>
          Chloris data is not pre-processed and may take up to 5 business days to process and
          deliver into your account. By placing this order, you agree to comply with the{' '}
          <a
            href="https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Chloris End User License Agreement linked here.
          </a>
        </p>
        <p>
          See our{' '}
          <a
            href="https://support.upstream.tech/article/170-chloris-forest-carbon"
            target="_blank"
            rel="noopener noreferrer"
          >
            support doc
          </a>{' '}
          for more details.
        </p>
      </>
    ),
  };
  return PACKAGE_ORDER_TEXT[layerKey];
};

export const ContractDetailsDescription: React.FC<{
  isDisabledSource: boolean;
  isProcessing?: boolean;
  sceneCoverageFailed: boolean;
  canPurchase: boolean;
  orderExceedsMaximumOrderAcres?: boolean;
  layerKey: string;
  sceneSensingTime?: string;
  orderGeometryAcres: number;
  billableAcres: number;
  imageryContract: ApiImageryContract;
  acresMessage: string;
  orderCents?: number; // For bulk orders, we use the value from the API response instead of calculating it on the frontend
  role: ApiOrganizationUser['role'];
  supportsCopyLink?: boolean; // Optional prop to indicate if the Copy Link button should be referenced for users who cannot order imagery. Defaults to true
}> = ({
  isDisabledSource,
  isProcessing,
  sceneCoverageFailed,
  canPurchase,
  orderExceedsMaximumOrderAcres,
  layerKey,
  sceneSensingTime,
  orderGeometryAcres,
  billableAcres,
  imageryContract,
  acresMessage,
  orderCents,
  role,
  supportsCopyLink = true,
}) => {
  const {sourceId} = parseLayerKey(layerKey);
  const sourceDetails = CONSTANTS.SOURCE_DETAILS_BY_ID[sourceId];
  const {prices} = useImageryPrices();
  const scenePricePerAcre: number | null = prices[sourceId];

  if (isDisabledSource) {
    return (
      <B.Callout icon={null} intent={B.Intent.DANGER}>
        <p>
          We’re sorry, we’re temporarily unable to process{' '}
          <strong>
            {sourceDetails.operator} {sourceDetails.name}
          </strong>{' '}
          data.
        </p>

        <p>
          Please check back soon or contact us at <strong>lens@upstream.tech</strong> with any
          questions.
        </p>
      </B.Callout>
    );
  }
  if (isProcessing) {
    return <p>We’re processing your order and will email you when it's ready.</p>;
  }
  if (sceneCoverageFailed) {
    return (
      <B.NonIdealState
        iconMuted={false}
        className={cs.sceneCoverageFailed}
        icon="warning-sign"
        description={
          <>
            <p>There is a problem with the geometry that prevents this scene from being ordered.</p>
            <p>
              Our engineers have been notified and will investigate. If this problem persists,
              please contact <strong>lens@upstream.tech</strong> for support.
            </p>
          </>
        }
      />
    );
  }
  if (!userUtils.mayOrderImagery(role)) {
    return (
      <B.Callout icon={null} intent={B.Intent.WARNING}>
        <p>Only organization admins may order data.</p>
        {supportsCopyLink && (
          <p>Click the Copy Link button to get a sharable URL for this scene.</p>
        )}
      </B.Callout>
    );
  }
  if (!scenePricePerAcre) {
    Sentry.captureException(
      `OrderImageryModal rendered scene without a price. SourceId: ${sourceId}`,
      {
        extra: {
          orderableSceneSource: sourceId,
          ...(sceneSensingTime && {orderableSceneSensingTime: sceneSensingTime}),
        },
      }
    );

    return (
      <B.Callout icon={null} intent={B.Intent.DANGER}>
        <p>
          We’re sorry, we’re temporarily unable to process{' '}
          <strong>
            {sourceDetails.operator} {sourceDetails.name}
          </strong>{' '}
          data.
        </p>

        <p>
          Please check back soon or contact us at <strong>lens@upstream.tech</strong> with any
          questions.
        </p>
      </B.Callout>
    );
  }
  return (
    <B.Callout icon={null} intent={canPurchase ? B.Intent.WARNING : B.Intent.DANGER}>
      <>
        <p>{acresMessage}</p>

        {orderGeometryAcres !== billableAcres && (
          <p>
            <strong>
              The minimum order amount for {sourceDetails.operator} {sourceDetails.name} is{' '}
              {formatCost(billableAcres * scenePricePerAcre)}.
            </strong>
          </p>
        )}
        {orderExceedsMaximumOrderAcres && (
          <p>
            <strong>
              This purchase exceeds the limit for automatic processing for {sourceDetails.operator}{' '}
              {sourceDetails.name}. Once you confirm below, our team will place the order on your
              behalf within 2 business days.
            </strong>
          </p>
        )}
        <DollarUnitContractDetails
          usage={imageryContract.usage as ApiDollarUnitUsage}
          acreage={billableAcres}
          layerKey={layerKey}
          pricePerAcre={scenePricePerAcre}
          orderCents={orderCents}
        />
      </>
    </B.Callout>
  );
};
